import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Moon, RefreshCw, Loader2, UserCheck, ClipboardList, CheckCircle2 } from 'lucide-react';
import { eod } from '../../lib/api';
import EodSnapshot from '../EodSnapshot';
import { toast } from 'sonner';
import { todayISO } from '../../lib/dates';

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const timeOf = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }); } catch { return ''; } };

export default function EndOfDay() {
  const [date, setDate] = useState(() => todayISO());
  const [snap, setSnap] = useState(null);
  const [clockouts, setClockouts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const [s, co] = await Promise.all([eod.report(d), eod.clockOuts(d).catch(() => [])]);
      setSnap(s); setClockouts(co || []);
    } catch { toast.error('Could not load the day report'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(date); }, [date, load]);

  return (
    <div className="p-6 lg:p-8 space-y-5">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2"><Moon className="w-7 h-7 text-[#0d542b]" />End of Day</h1>
          <p className="text-slate-500 mt-1">Daily workday snapshot across all technicians.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10 w-44" />
          <Button variant="outline" onClick={() => load(date)} className="gap-1.5"><RefreshCw className="w-4 h-4" />Refresh</Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : (
        <>
          <EodSnapshot snap={snap} />

          {/* The day's finished visits that still owe their service report. Each
              one is a job with no record of what was applied, and an invoice being
              held until there is — so this is the list that has to be empty before
              anyone goes home. */}
          <Card className="p-5 border-slate-200">
            <div className="flex items-center gap-2 font-bold text-slate-900 mb-3">
              <ClipboardList className="w-4 h-4 text-[#8dd110]" />
              Awaiting service report ({snap?.jobs?.awaitingReport || 0})
            </div>
            {!snap?.jobs?.awaitingReportList?.length ? (
              <p className="text-sm text-slate-500 flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                Every completed visit has its report filed.
              </p>
            ) : (
              <>
                <p className="text-xs text-slate-500 mb-2">These jobs are done but not billed. The invoice is raised as soon as the technician files the report.</p>
                <div className="divide-y divide-slate-100">
                  {snap.jobs.awaitingReportList.map((j) => (
                    <div key={j.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5" data-testid={`awaiting-report-${j.id}`}>
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 truncate">{j.customer || 'Customer'}</div>
                        <div className="text-xs text-slate-500">{[j.service, j.time, j.tech].filter(Boolean).join(' · ')}</div>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {(j.missing || []).map((m) => (
                          <Badge key={m} className="bg-amber-100 text-amber-800 border-0 font-normal">Needs {m}</Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Card>

          <Card className="p-5 border-slate-200">
            <div className="flex items-center gap-2 font-bold text-slate-900 mb-3"><UserCheck className="w-4 h-4 text-[#8dd110]" />Technician clock-outs ({clockouts.length})</div>
            {clockouts.length === 0 ? (
              <p className="text-sm text-slate-400">No technicians have clocked out for this day yet. They tap “End My Day” in the field app.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {clockouts.map((c) => (
                  <div key={c.id} className="flex items-center justify-between py-2.5">
                    <div>
                      <div className="font-semibold text-slate-900">{c.techName || c.email}</div>
                      <div className="text-xs text-slate-500">Clocked out {timeOf(c.at)}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-emerald-100 text-emerald-700 border-0">{c.completed}/{c.scheduled} done</Badge>
                      {c.remaining > 0 && <Badge className="bg-amber-100 text-amber-700 border-0">{c.remaining} left</Badge>}
                      {c.awaitingReport > 0 && <Badge className="bg-red-100 text-red-700 border-0">{c.awaitingReport} no report</Badge>}
                      <Badge className="bg-slate-100 text-slate-700 border-0">{money(c.collected)}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
